import { useEffect } from "react";
import docStyles from "../../css/Docs/Docs.module.css";
import CodeBlock from "./CodeBlock";
import { Divider } from "@mui/material";
import DocWrapper from "./DocWrapper";
import { Link } from "react-router-dom";

const curlSample = `
~~~curl
curl --location --request PUT 'https://test.eqlfin.com/activities/create' 
--header 'Content-Type: application/json' 
--header 'Firebase-Env: env/core' 
--header 'Authorization: Basic {{token}}' 
--data-raw '{
    "timestamp": 1683101433339,
    "venue": [
        {
            "venueDescriptor": "CUSTOMER_OFFICE",
            "geopoint": {
                "latitude": 0,
                "longitude": 0
            },
            "location": "",
            "address": ""
        }
    ],
    "schedule": [],
    "office": "OFFICE_NAME",
    "share": [],
    "template": "customer",
    "attachment": {
        "firstContactEmail": {
            "type": "string",
            "value": "name@company.com"
        },
        "gstNumber": {
            "type": "gst",
            "value": "GST_NUMBER"
        },
        "supplierAddress": {
            "type": "string",
            "value": ""
        },
        "tds": {
            "type": "number",
            "value": 10
        },
        "moratium": {
            "type": "number",
            "value": 20
        },
        "firstContactName": {
            "type": "string",
            "value": "NAME"
        },
        "earlyPaymentRate": {
            "type": "number",
            "value": 50
        },
        "firstContact": {
            "type": "phoneNumber",
            "value": "+919971110111"
        },
        "vendorCode": {
            "type": "string",
            "value": "VENDOR_TRADE_NAME"
        }
    }
}'
~~~
`;

const Customer = () => {
  useEffect(() => {
    const el = document.getElementById("introduction");
    if (el) {
      el.scrollIntoView();
    }
  }, []);

  return (
    <DocWrapper>
      <div className={docStyles.container}>
        <section id="introduction" className={docStyles.section}>
          <div className={docStyles.heading}>Introduction</div>
          <div className={docStyles.para}>
            The Create Customer API allows offices registered with our platform
            to create customers or clients. This API enables the creation of
            buyer or seller profiles within the requesting office. The provided
            API endpoint ensures secure access and restricted functionality
            limited to authorised offices.
          </div>
        </section>

        <br />
        <Divider sx={{ width: "100%" }} />

        <section id="fields" className={docStyles.section}>
          <div className={docStyles.heading}>Important Fields</div>
          <div className={docStyles.para}>
            To successfully create a customer or client for an existing office,
            the following field information is crucial:
            <ul
              style={{
                padding: "0px 0px 5px 10px",
                margin: "5px 0px 5px 10px",
              }}
            >
              <li>office</li>
              <li>gstNumber</li>
              <li>firstContactEmail</li>
              <li>firstContactName</li>
              <li>firstContact</li>
            </ul>
          </div>
        </section>

        <Divider sx={{ width: "100%" }} />

        <section id="request-example" className={docStyles.section}>
          <div className={docStyles.heading}>Request Example</div>
          <div className={docStyles.para}>
            This is a sample curl request
            <CodeBlock codeMarkdown={curlSample} />
          </div>

          <div className={docStyles.heading} id="schema-description">
            Schema Description
          </div>
          <table className={docStyles.markDownTable}>
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
                <th>Data Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>timestamp</td>
                <td>1683971721265</td>
                <td>number</td>
                <td>time of the creation</td>
              </tr>
              <tr>
                <td>
                  <code>schedule</code>
                </td>
                <td>can be empty</td>
                <td>array</td>
                <td>schedule</td>
              </tr>
              <tr>
                <td>
                  <code>office</code>
                </td>
                <td>OFFICE_NAME</td>
                <td>String</td>
                <td>name of the requesting client office</td>
              </tr>
              <tr>
                <td>
                  <code>share</code>
                </td>
                <td>can be empty</td>
                <td>array</td>
                <td>N/A</td>
              </tr>
              <tr>
                <td>
                  <code>template</code>
                </td>
                <td>customer</td>
                <td>string</td>
                <td>structure of the API call, here customer</td>
              </tr>
            </tbody>
          </table>
          <div className={docStyles.headingTwo}>venue</div>
          <table className={docStyles.markDownTable}>
            <thead>
              <tr>
                <th>Key</th>
                <th>Value (default)</th>
                <th>Data Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>venueDescriptor</code>
                </td>
                <td>Customer Office</td>
                <td>String</td>
                <td>requesting client office</td>
              </tr>
              <tr>
                <td>
                  <code>geopoint</code>
                </td>
                <td>can be 0 and 0</td>
                <td>Object</td>
                <td>
                  <pre>
                    &quot;latitude&quot; : &quot;&quot;, &quot;longitude&quot;:
                    &quot;&quot;
                  </pre>
                </td>
              </tr>
              <tr>
                <td>
                  <code>location</code>
                </td>
                <td>can be empty</td>
                <td>String</td>
                <td>Location of the requesting client office</td>
              </tr>
              <tr>
                <td>
                  <code>address</code>
                </td>
                <td>can be empty</td>
                <td>String</td>
                <td>Address of the requesting client office</td>
              </tr>
            </tbody>
          </table>
          <div className={docStyles.headingTwo}>attachment</div>
          <table className={docStyles.markDownTable}>
            <thead>
              <tr>
                <th>Key</th>
                <th>Value (default)</th>
                <th>Data Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>firstConatctEmail</code>
                </td>
                <td>name@company.com</td>
                <td>String</td>
                <td>email id of the customer</td>
              </tr>
              <tr>
                <td>
                  <code>gstNumber</code>
                </td>
                <td>GST_NUMBER</td>
                <td>gst (String)</td>
                <td>valid gst number of the customer</td>
              </tr>
              <tr>
                <td>
                  <code>supplierAddress</code>
                </td>
                <td>empty</td>
                <td>String</td>
                <td>Address of the customer</td>
              </tr>
              <tr>
                <td>
                  <code>tds</code>
                </td>
                <td>0</td>
                <td>number/int/float</td>
                <td>tds to be applied</td>
              </tr>
              <tr>
                <td>
                  <code>moratium</code>
                </td>
                <td>0</td>
                <td>number/int/float</td>
                <td>Credit days</td>
              </tr>
              <tr>
                <td>
                  <code>firstConatctName</code>
                </td>
                <td>NAME</td>
                <td>String</td>
                <td>Name name of the customer</td>
              </tr>
              <tr>
                <td>
                  <code>earlyPaymentRate</code>
                </td>
                <td>50</td>
                <td>number/int/float</td>
                <td>Rs. per lakh per day</td>
              </tr>
              <tr>
                <td>
                  <code>firstContact</code>
                </td>
                <td>+91{`PHONE_NUMBER`}</td>
                <td>String</td>
                <td>phone number of the customer</td>
              </tr>
              <tr>
                <td>
                  <code>vendorCode</code>
                </td>
                <td>VENDOR_TRADE_NAME</td>
                <td>String</td>
                <td>vendor code given by the requesting client office</td>
              </tr>
            </tbody>
          </table>

          <br />
          <Divider sx={{ width: "100%" }} />

          <section id="response" className={docStyles.section}>
            <div className={docStyles.heading}>Response</div>
            <div className={docStyles.para}>
              Upon successful execution of the Create Customer API, a response
              containing the newly created customer's activityID or other
              relevant information associated with the customer will be
              returned.
            </div>

            <div className={docStyles.subHeading}>201 - Created</div>
            <CodeBlock
              codeMarkdown={`
~~~json
{
  "activityId": "tCjCYULZVQLVHlXbAGmB",
  "message": "",
  "success": true,
  "code": 201
}
~~~
`}
            />
          </section>
          <br />
          <Divider sx={{ width: "100%" }} />

          <section id="error" className={docStyles.section}>
            <div className={docStyles.heading}>Error Responses</div>

            <div className={docStyles.para}>
              In case of errors during the API call, you may receive one of the
              following error responses:
              <ul
                style={{
                  padding: "0px 0px 5px 10px",
                  margin: "5px 0px 5px 10px",
                }}
              >
                <li>
                  <b>Unauthorized Error: </b> Occurs when the request lacks
                  valid authentication or authorization credentials.
                </li>
                <li>
                  <b> Server Error: </b> Indicates an unexpected error on the
                  server side, such as a database connection issue or an
                  unhandled exception.
                </li>
              </ul>
            </div>

            <div className={docStyles.subHeading}>400 - Bad Request</div>
            <CodeBlock
              codeMarkdown={`
~~~json
{
    "message": "Invalid value for the field 'gstNumber' in attachment object",
    "success": false,
    "code": 400
}
~~~
`}
            />
            <CodeBlock
              codeMarkdown={`
~~~json
{
    "message": "The 'firstContact' field should be a non-empty string.",
    "success": false,
    "code": 400
}
~~~
`}
            />

            <div className={docStyles.subHeading}>403 - Forbidden</div>
            <CodeBlock
              codeMarkdown={`
~~~json
{
    "message": "Customer with GST is already exists with this Office",
    "success": false,
    "code": 403
}
~~~
`}
            />
          </section>
        </section>
      </div>
      <section className={docStyles.tableOfContentSection}>
        <div className={docStyles.tableOfContent}>
          <div className={docStyles.tocHeading}>Table of contents</div>
          <ul className={docStyles.tocList}>
            <li>
              <Link to="#introduction">Introduction</Link>
            </li>
            <li>
              <Link to="#fields">Important Fields</Link>
            </li>
            <li>
              <Link to="#request-example">Request Example</Link>
            </li>
            <li>
              <Link to="#schema-description">Schema Description</Link>
            </li>
            <li>
              <Link to="#response">Response</Link>
            </li>
            <li>
              <Link to="#error">Error Response</Link>
            </li>
          </ul>
        </div>
      </section>
    </DocWrapper>
  );
};

export default Customer;
